import { useGenerateDeepLink } from "@src/appV2/DeepLinks";
import { environmentConfig } from "@src/appV2/environment";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { openInBrowser } from "@src/appV2/NativeAdapters/browser.adapter";
import { generateStripeReturnUrl } from "@src/appV2/Stripe/generateStripeReturnUrl";
import { useCallback } from "react";
import { useLocation } from "react-router-dom";

import { useGenerateStripeExpressSignInLink } from "../api/useGenerateStripeExpressSignInLink";

export function useGoToStripeExpressSignIn(): {
  goToStripeExpressSignIn: () => void;
  isGoToStripeExpressSignInLoading: boolean;
} {
  const { showErrorToast } = useToast();
  const location = useLocation();
  const stripeReturnLinkForceRedirect = useCbhFlag(
    CbhFeatureFlag.STRIPE_RETURN_LINK_FORCE_REDIRECT,
    {
      defaultValue: false,
    }
  );

  const {
    mutateAsync: generateStripeExpressSignInLink,
    isLoading: isGenerateStripeExpressSignInLinkLoading,
  } = useGenerateStripeExpressSignInLink();

  const { mutateAsync: generateDeepLink, isLoading: isGenerateDeepLinkLoading } =
    useGenerateDeepLink();

  const isGoToStripeExpressSignInLoading =
    isGenerateDeepLinkLoading || isGenerateStripeExpressSignInLinkLoading;

  const goToStripeExpressSignIn = useCallback(async () => {
    try {
      const returnUrl = await generateStripeReturnUrl({
        flow: "payout-settings-express-sign-in",
        generateDeepLink,
        link: `${environmentConfig.REACT_APP_WEB_APP_URL}${location.pathname}`,
        purpose: "return-url",
        skipPreview: stripeReturnLinkForceRedirect,
      });
      const { data: link } = await generateStripeExpressSignInLink({ returnUrl });

      await openInBrowser({ url: link });
    } catch (error) {
      showErrorToast("Failed to open Stripe, please try again!");
      logEvent(APP_V2_APP_EVENTS.GENERATE_STRIPE_EXPRESS_SIGN_IN_LINK_FAILURE, { error });
    }
  }, [
    generateDeepLink,
    generateStripeExpressSignInLink,
    showErrorToast,
    stripeReturnLinkForceRedirect,
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- Intentionally not including location.pathname
  ]);

  return { goToStripeExpressSignIn, isGoToStripeExpressSignInLoading };
}
